import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { useOnSiteStructureChanged } from "../../../hooks/useOnSiteStructureChanged";
import urls from "../../../urls";
import { COMPONENT_QUERY_KEY } from "../../../utils/constants";
import { ObjectName } from "../../../utils/enums";
import type {
  ConsumerCreateRequest,
  ConsumerResponse,
  ConsumerUpdateRequest
} from "../Consumer.types";
import { consumerQueries } from "./useConsumer";

export function useConsumerMutation(siteId: number) {
  const queryClient = useQueryClient();
  const cacheName = COMPONENT_QUERY_KEY[ObjectName.Consumer];
  const onSiteStructureChanged = useOnSiteStructureChanged(siteId);

  const createMutation = useMutation({
    mutationFn: (createRequest: ConsumerCreateRequest) =>
      createConsumer(createRequest, siteId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      onSiteStructureChanged();
    }
  });

  const editMutation = useMutation({
    mutationFn: (updateRequest: ConsumerUpdateRequest) =>
      editConsumer(updateRequest),
    onSuccess(response) {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: consumerQueries.detail(response.data.id).queryKey
      });
      onSiteStructureChanged();
    }
  });

  return { createMutation, editMutation };
}

function createConsumer(data: ConsumerCreateRequest, siteId: number) {
  const url = urls.apiWithoutCamelization.consumers(siteId);
  return apiWithoutCamelization.post<ConsumerResponse>(url, data);
}

function editConsumer(data: ConsumerUpdateRequest) {
  const url = urls.apiWithoutCamelization.consumer(data.id);
  return apiWithoutCamelization.patch<ConsumerResponse>(url, data);
}
